import { useEffect, useRef, useMemo, MutableRefObject } from 'react';
import * as d3Drag from 'd3-drag';
import * as d3 from 'd3-selection';
import * as d3Zoom from 'd3-zoom';
import 'd3-dispatch';

import { CustomEvents } from '@labradorsports/constants';
import {
    findClosestNode,
    applyOffset,
    getMoveFromAnchor,
    usePlayEditor,
    useFieldViewport,
    usePlayback,
    useSelected,
    weightedDistance,
    flipPoint,
    FrameSnapshot,
    FieldPiece,
    areClose,
    clampZoomTransform,
} from '@labradorsports/play-rendering';

import { useFieldContainer, usePlugins } from '../providers/index.js';

interface UsePieceDragProps {
    setTempFrames: (frames: FrameSnapshot[] | null) => void;
    setManipulatingPieces: (pieces: FieldPiece[]) => void;
    dragSubjectRef: MutableRefObject<any>;
}

export default function usePieceDrag({ setTempFrames, setManipulatingPieces, dragSubjectRef }: UsePieceDragProps) {
    const { containerRef } = useFieldContainer();
    // We expect play to always be defined in this component
    const { play, currentFrame, currentFrameIdx, PlayConfig, selectFrame, readOnly, debugOptions, editMode } =
        usePlayEditor();
    const { fieldViewport, setZoomTransform, zoomTransform } = useFieldViewport();
    const { playing } = usePlayback();

    const { selected } = useSelected();

    const dragAdjustmentRef = useRef<any>();

    const { anchors, handleDrag, updatePiece } = usePlugins();

    const setDragAdjustment = ({ x = 0, y = 0 }) => {
        dragAdjustmentRef.current = {
            x: (dragAdjustmentRef.current?.x ?? 0) + x,
            y: (dragAdjustmentRef.current?.y ?? 0) + y,
        };
    };

    // These handlers use separate effects so each can only be updated when necessary
    // They are stored as refs so that the drag handler can be updated mid-drag as the frame changes
    const dragHandler = useRef<AnyFunction>();
    const dragEndHandler = useRef<AnyFunction>();
    const dragSubjectHandler = useRef<AnyFunction>();

    useEffect(() => {
        dragHandler.current = (event) => {
            const anc = event.subject;
            // Event coordinates are based on subject, so we do not need to modify them based on zoomTransform

            if (event.y + zoomTransform.y < 30) {
                setDragAdjustment({
                    y: -3,
                });
            } else if (event.x + zoomTransform.x < 30) {
                setDragAdjustment({
                    x: -3,
                });
            } else if (-zoomTransform.x + fieldViewport.dimensions.width - event.x < 30) {
                setDragAdjustment({
                    x: 3,
                });
            } else if (-zoomTransform.y + fieldViewport.dimensions.height - event.y < 30) {
                setDragAdjustment({
                    y: 3,
                });
            }

            const tempFrames = handleDrag(
                anc,
                fieldViewport.getFieldCoords(applyOffset(event, dragAdjustmentRef.current ?? { x: 0, y: 0 }))
            );

            setTempFrames(tempFrames);
            const move = getMoveFromAnchor(anc, currentFrame, PlayConfig.MoveConfig);
            setManipulatingPieces(move ? [move] : []);
        };
    }, [currentFrame, play, zoomTransform, fieldViewport, debugOptions, currentFrameIdx]);

    useEffect(() => {
        dragEndHandler.current = (event) => {
            const dragOffset = dragAdjustmentRef.current ?? { x: 0, y: 0 };
            const anc = event.subject;
            const unclamped = fieldViewport.getFieldCoords(applyOffset(event, dragOffset));
            // Event coordinates are based on subject, so we do not need to modify them based on zoomTransform

            if (
                // drawing
                anc.pointerType === 'pen' ||
                // Click
                (areClose(event.x, anc.startX, 3) && areClose(event.y, anc.startY, 3))
            ) {
                setTempFrames(null);
                setManipulatingPieces([]);
                return;
            }

            const { x, y, type, id, target, frameIdx, origin, props } = anc;

            const ancCopy = {
                type,
                id,
                target,
                frameIdx,
                origin,
                props,
                ...applyOffset({ x, y }, dragOffset),
            };

            if (typeof frameIdx === 'number' && frameIdx !== currentFrameIdx) {
                selectFrame(frameIdx);
            }

            updatePiece(ancCopy, unclamped);

            requestAnimationFrame(() => {
                setTempFrames(null);
                setManipulatingPieces([]);
            });

            if (dragAdjustmentRef.current) {
                setZoomTransform(
                    clampZoomTransform(
                        fieldViewport,
                        fieldViewport.zoomFactor,
                        fieldViewport.zoomFactor,
                        new d3Zoom.ZoomTransform(
                            zoomTransform.k,
                            zoomTransform.x - dragOffset.x,
                            zoomTransform.y - dragOffset.y
                        )
                    )
                );

                dragAdjustmentRef.current = undefined;
            }
        };
    }, [currentFrame, play, zoomTransform, fieldViewport, currentFrameIdx]);

    useEffect(() => {
        dragSubjectHandler.current = (event) => {
            if (!playing && editMode && !readOnly && anchors.length > 0) {
                const finalAnchors = anchors.filter(
                    (anchor) => typeof anchor.pointerType === 'undefined' || anchor.pointerType === event.pointerType
                );

                // We must adjust the coordinates of the event here because d3 will use the coordinates in the parent element until subject is selected
                // After subject is specified, the event coordinates will be based on the coordinates of the subject
                const coords = applyOffset(event, flipPoint(zoomTransform));
                const closest = findClosestNode(coords, finalAnchors);

                if (closest && weightedDistance(closest, coords, closest.distanceBias) < 30) {
                    return {
                        ...closest,
                        startX: closest.x,
                        startY: closest.y,
                    };
                }
            }

            return null;
        };
    }, [selected, fieldViewport, playing, editMode, readOnly, anchors, zoomTransform, currentFrameIdx]);

    const drag = useMemo(() => {
        return (
            d3Drag
                .drag()
                // Force touchable for all devices to fix Windows 10 touch
                .touchable(() => true)
                .on('start', (event: d3Drag.D3DragEvent<any, any, any>) => {
                    if (event.subject && event.subject.pointerType !== 'pen') {
                        containerRef.current.dispatchEvent(new Event(CustomEvents.PIECE_DRAG_START));
                    }
                })
                .on('drag', (event) => {
                    dragHandler.current(event);
                })
                .on('end', (event) => {
                    dragEndHandler.current(event);
                    dragSubjectRef.current = undefined;
                })
                .subject((event) => {
                    // Only a single subject can be active at a time
                    if (event.active === 0) {
                        const subject = dragSubjectHandler.current(event);
                        dragSubjectRef.current = subject;
                        return subject;
                    }

                    return null;
                })
        );
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            d3.select(containerRef.current).call(drag);

            return () => {
                d3.select(containerRef.current).on('.drag', null);
            };
        }

        return () => {
            //
        };
    }, [containerRef.current]);

    return {
        dragAdjustmentRef,
    };
}
